/**
 * @fileoverview Orders Store (Zustand)
 * 
 * Manages order state: cart items, billing, currency, etc.
 * Replaces OrdersContext with better performance.
 * 
 * @module stores/useOrdersStore
 */

import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';
import { getOrderStatus } from '@/api';
import { getRestaurantCurrencySync } from '@/utils/currency';
import { wsService } from '@/services/websocket';

/**
 * Format order data helper
 */
const formatOrderData = (initialOrder) => {
  return initialOrder.map((orderItem) => {
    const formattedOrder = {
      category_id: orderItem.category_id || 1,
      name: orderItem.name,
      image: orderItem.image,
      quantity: orderItem.quantity,
      price: {
        id: orderItem.price.id,
        price: orderItem.price.price,
      },
      ready_time: orderItem.ready_time,
      questions: orderItem.questions.map((question) => ({
        id: question.id,
        question: question.question,
        answers: question.answers.map((answer) => {
          const answerKey = Object.keys(answer).find((key) => key !== 'selected');
          return {
            name: answerKey,
            price: parseInt(answer[answerKey], 10),
            selected: answer.selected,
          };
        }),
      })),
      items: orderItem.items || [],
      options: orderItem.options || [],
      recommendations: orderItem.recommendations || [],
      selectedOptions: orderItem.selectedOptions || [],
      selectedResponses:
        orderItem.selectedResponses ||
        orderItem.questions.map((question) => ({
          itemId: orderItem.id,
          responses: {
            [question.id]: {
              question: question.question,
              response: question.answers
                .filter((answer) => answer.selected)
                .map((answer) => {
                  const answerKey = Object.keys(answer).find((key) => key !== 'selected');
                  return answerKey;
                }),
              price: question.answers
                .filter((answer) => answer.selected)
                .map((answer) => {
                  const answerKey = Object.keys(answer).find((key) => key !== 'selected');
                  return answer[answerKey];
                }),
            },
          },
        })),
      uuid: orderItem.uuid || uuidv4(),
      is_available: orderItem.is_available !== undefined ? orderItem.is_available : true,
      highlight: orderItem.highlight !== undefined ? orderItem.highlight : false,
      speciality: orderItem.speciality !== undefined ? orderItem.speciality : false,
    };

    return formattedOrder;
  });
};

/**
 * Orders Store
 * 
 * @example
 * const orderItem = useOrdersStore((state) => state.orderItem);
 * const handleAddOrderItem = useOrdersStore((state) => state.handleAddOrderItem);
 * 
 * // Or use the legacy hook for backward compatibility
 * const { orderItem, handleAddOrderItem, currency } = useOrders();
 */
export const useOrdersStore = create((set, get) => ({
  // State
  search: '',
  recommendData: [],
  trendingData: [],
  confirmedOrder: [],
  totalBillingPrice: '0.00',
  totalQuantity: 0,
  orderItem: [],
  discount: '0',
  selectedResponses: [],
  currency: 'USD',
  defaultLanguage: 'en',
  addons_price: 0,
  
  // Snackbar state
  snackbar: { open: false, message: '', severity: 'success' },

  // Internal state
  _initialized: false,
  _wsCleanup: null,
  _cleanup: null,

  // Setters
  setSearch: (search) => set({ search }),
  setRecommendData: (recommendData) => set({ recommendData }),
  setTrendingData: (trendingData) => set({ trendingData }),
  setConfirmedOrder: (confirmedOrder) => {
    set({ confirmedOrder });
    if (typeof window !== 'undefined') {
      localStorage.setItem('confirmedOrder', JSON.stringify(confirmedOrder));
    }
  },
  setTotalBillingPrice: (totalBillingPrice) => set({ totalBillingPrice }),
  setTotalQuantity: (totalQuantity) => set({ totalQuantity }),
  setOrderItem: (orderItem) => set({ orderItem: typeof orderItem === 'function' ? orderItem(get().orderItem) : orderItem }),
  setDiscount: (discount) => set({ discount }),
  setSelectedResponses: (selectedResponses) => set({ selectedResponses: typeof selectedResponses === 'function' ? selectedResponses(get().selectedResponses) : selectedResponses }),
  setCurrency: (currency) => set({ currency }),
  setDefaultLanguage: (defaultLanguage) => set({ defaultLanguage }),
  setAddonsPrice: (addons_price) => set({ addons_price }),
  setSnackbar: (snackbar) => set({ snackbar }),

  // Close snackbar
  closeSnackbar: () => set({ snackbar: { open: false, message: '', severity: 'success' } }),

  // Fetch order status
  fetchOrderStatus: async () => {
    try {
      const order_id = typeof window !== 'undefined' ? localStorage.getItem('order_id') : null;
      if (order_id) {
        const statusResponse = await getOrderStatus(order_id);
        if (statusResponse?.status === 1 || statusResponse?.status === 2) {
          localStorage.removeItem('order_id');
          set({
            confirmedOrder: [],
            totalBillingPrice: '0.00',
            addons_price: 0
          });
          localStorage.removeItem('confirmedOrder');
        } else if (statusResponse?.status === 1 || statusResponse?.status === 0) {
          if (statusResponse?.discount !== '0') {
            set({ discount: statusResponse.discount });
          }
          if (statusResponse?.menus) {
            const formattedMenus = formatOrderData(statusResponse.menus);
            set({ confirmedOrder: [formattedMenus] });
            localStorage.setItem('confirmedOrder', JSON.stringify([formattedMenus]));
          }
        }
      }
    } catch (error) {
      console.error('Error fetching order status:', error);
    }
  },

  // Handle add order item
  handleAddOrderItem: (item) => {
    const { selectedResponses, recommendData } = get();
    
    set((state) => {
      let newOrderItem;
      
      if (selectedResponses.length === 0) {
        const existingItemIndex = state.orderItem.findIndex((orderItem) => orderItem.id === item.id);
        if (existingItemIndex !== -1) {
          const updatedOrder = [...state.orderItem];
          updatedOrder[existingItemIndex].quantity += 1;
          newOrderItem = updatedOrder;
        } else {
          const uniqueItem = {
            ...item,
            uuid: uuidv4(),
            selectedResponses: [...selectedResponses],
            quantity: 1,
            recommended: item.recommended || false
          };
          newOrderItem = [...state.orderItem, uniqueItem];
        }
      } else {
        const uniqueItem = {
          ...item,
          uuid: uuidv4(),
          selectedResponses: [...selectedResponses],
          quantity: 1,
          recommended: item.recommended || false
        };
        newOrderItem = [...state.orderItem, uniqueItem];
      }

      // Filter out from recommend data if exists
      const newRecommendData = recommendData.some((p) => p.id === item.id)
        ? recommendData.filter((p) => p.id !== item.id)
        : recommendData;

      return {
        orderItem: newOrderItem,
        recommendData: newRecommendData,
        selectedResponses: []
      };
    });
  },

  // Initialize currency
  initializeCurrency: async () => {
    try {
      const { getRestaurantCurrency } = await import('@/utils/currency');
      const currentCurrency = await getRestaurantCurrency();
      set({ currency: currentCurrency });
    } catch (error) {
      console.error("Error initializing currency:", error);
      set({ currency: getRestaurantCurrencySync() });
    }
  },

  // Connect WebSocket for order updates
  connectWebSocket: () => {
    // Try both 'session' (admin app uses this) and 'session_token' (enduser might use this)
    let session_token = typeof window !== 'undefined' ? localStorage.getItem('session_token') : null;
    
    if (!session_token) return;
    
    // Already connected
    if (get()._wsCleanup) return;


    const handleUpdateDiscount = (data) => {
      if (data.message === 'Discount applied') {
        const discountValue = data.discount ? data.discount : '0';
        set({
          discount: discountValue,
          snackbar: {
            open: true,
            message: 'Discount has been applied.',
            severity: 'success',
          }
        });
      } else {
        set({
          snackbar: {
            open: true,
            message: 'Order has been edited.',
            severity: 'success',
          }
        });
      }
    };

    const handleUpdateOrder = () => {
      set({
        snackbar: {
          open: true,
          message: 'Order has been updated.',
          severity: 'info',
        }
      });
      get().fetchOrderStatus();
    };

    // Only this order session's room: the admin room also carries order::update.
    const room = `enduser-restaurant:${session_token}`;
    const release = wsService.join(room, { onResync: () => get().fetchOrderStatus() });
    const offs = [
      wsService.on('order::update-discount', handleUpdateDiscount, { room }),
      wsService.on('order::update', handleUpdateOrder, { room }),
    ];

    set({
      _wsCleanup: () => {
        offs.forEach((off) => off());
        release();
      }
    });
  },

  // Initialize store
  initialize: () => {
    if (get()._initialized) return;

    // Initialize currency
    get().initializeCurrency();

    // Fetch initial order status
    get().fetchOrderStatus();

    // Setup visibility change handler
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        try {
          const order_id = localStorage.getItem('order_id');
          if (order_id) {
            const statusResponse = await getOrderStatus(order_id);
            if (statusResponse?.discount !== '0') {
              set({ discount: statusResponse.discount });
            }
            if (statusResponse?.menus) {
              const formattedMenus = formatOrderData(statusResponse.menus);
              set({ confirmedOrder: [formattedMenus] });
              localStorage.setItem('confirmedOrder', JSON.stringify([formattedMenus]));
            }
            if (statusResponse?.status === 1 || statusResponse?.status === 2) {
              localStorage.removeItem('order_id');
              set({
                confirmedOrder: [],
                totalBillingPrice: '0.00',
                addons_price: 0
              });
              localStorage.removeItem('confirmedOrder');
            }
          }
        } catch (error) {
          console.error('Error fetching order status:', error);
        }
      }
    };

    // Setup currency change handlers
    const handleCurrencyChange = async () => {
      try {
        const { getRestaurantCurrency } = await import('@/utils/currency');
        const newCurrency = await getRestaurantCurrency();
        set({ currency: newCurrency });
      } catch (error) {
        console.error("Error updating currency:", error);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('currencyChanged', handleCurrencyChange);
    window.addEventListener('storage', handleCurrencyChange);

    // Try to connect WebSocket if session exists
    get().connectWebSocket();

    // Listen for storage changes to detect when session is set
    const handleStorageForWs = (e) => {
      if (e?.key === 'session_token' && e?.newValue) {
        get().connectWebSocket();
      }
    };
    window.addEventListener('storage', handleStorageForWs);

    set({
      _initialized: true,
      _cleanup: () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('currencyChanged', handleCurrencyChange);
        window.removeEventListener('storage', handleCurrencyChange);
        window.removeEventListener('storage', handleStorageForWs);
        const wsCleanup = get()._wsCleanup;
        if (wsCleanup) wsCleanup();
      }
    });
  },

  // Cleanup
  cleanup: () => {
    const cleanupFn = get()._cleanup;
    if (cleanupFn) cleanupFn();
    set({ _initialized: false, _cleanup: null, _wsCleanup: null });
  },

  // Reset store
  reset: () => {
    get().cleanup();
    set({
      search: '',
      recommendData: [],
      trendingData: [],
      confirmedOrder: [],
      totalBillingPrice: '0.00',
      totalQuantity: 0,
      orderItem: [],
      discount: '0',
      selectedResponses: [],
      currency: 'USD',
      defaultLanguage: 'en',
      addons_price: 0,
      snackbar: { open: false, message: '', severity: 'success' },
      _initialized: false,
      _wsCleanup: null,
      _cleanup: null
    });
  }
}));

// Auto-initialize on module load (client-side only)
if (typeof window !== 'undefined') {
  setTimeout(() => {
    useOrdersStore.getState().initialize();
  }, 0);
}

/**
 * Legacy hook for backward compatibility
 * Use this hook to get the same API as the old OrdersContext
 * 
 * @example
 * const { orderItem, handleAddOrderItem, currency } = useOrders();
 */
export const useOrders = () => {
  const store = useOrdersStore();
  
  return {
    search: store.search,
    setSearch: store.setSearch,
    recommendData: store.recommendData,
    setRecommendData: store.setRecommendData,
    trendingData: store.trendingData,
    setTrendingData: store.setTrendingData,
    confirmedOrder: store.confirmedOrder,
    setConfirmedOrder: store.setConfirmedOrder,
    totalBillingPrice: store.totalBillingPrice,
    setTotalBillingPrice: store.setTotalBillingPrice,
    totalQuantity: store.totalQuantity,
    setTotalQuantity: store.setTotalQuantity,
    orderItem: store.orderItem,
    setOrderItem: store.setOrderItem,
    discount: store.discount,
    setDiscount: store.setDiscount,
    selectedResponses: store.selectedResponses,
    setSelectedResponses: store.setSelectedResponses,
    currency: store.currency,
    setCurrency: store.setCurrency,
    defaultLanguage: store.defaultLanguage,
    setDefaultLanguage: store.setDefaultLanguage,
    addons_price: store.addons_price,
    setAddonsPrice: store.setAddonsPrice,
    handleAddOrderItem: store.handleAddOrderItem,
    snackbar: store.snackbar,
    closeSnackbar: store.closeSnackbar,
    connectWebSocket: store.connectWebSocket
  };
};
